import type { TasksMapBoardData, TasksMapBoardParams } from './TasksMapBoard'
import type { TasksMapMetaData } from './TasksMapMeta'
import type { TasksMapPersonInput } from './tasksMap'

interface MockApiResponse<T> {
  code: number
  data: T
  msg: string
}

export const MOCK_TASKS_MAP_META: TasksMapMetaData = {
  tabs: [
    { value: 'inspector', label: 'Inspectors' },
    { value: 'technician', label: 'Technician' },
  ],
}

const MOCK_INSPECTORS: TasksMapPersonInput[] = [
  {
    id: '12',
    name: 'Alex Chen',
    initials: 'AC',
    orderCount: 1,
    position: { lat: 49.2827, lng: -123.1207 },
    orders: [
      {
        id: '121',
        category: 'essential',
        address: '328 Maple Ridge Dr',
        orderId: 'WC251215000001',
        time: '09:00AM - 12:00PM',
        position: { lat: 49.2827, lng: -123.1207 },
        sequence: 1,
      },
    ],
  },
  {
    id: '13',
    name: 'Jamie Lee',
    initials: 'JL',
    orderCount: 2,
    position: { lat: 49.2500, lng: -123.1000 },
    orders: [
      {
        id: '122',
        category: 'enhanced',
        address: '18 Birchwood Ave',
        orderId: 'WC251215000002',
        time: '08:00AM - 10:00AM',
        position: { lat: 49.2510, lng: -123.1010 },
        sequence: 1,
      },
      {
        id: '123',
        category: 'premium',
        address: '52 Meadowbrook Rd',
        orderId: 'WC251215000003',
        time: '10:30AM - 12:30PM',
        position: { lat: 49.2490, lng: -123.0990 },
        sequence: 2,
      },
    ],
  },
]

const MOCK_TECHNICIANS: TasksMapPersonInput[] = [
  {
    id: '35',
    name: 'David Kim',
    initials: 'DK',
    orderCount: 2,
    position: { lat: 49.2600, lng: -123.1100 },
    orders: [
      {
        id: '2001',
        category: 'essential',
        address: '12 Oakridge Cres',
        orderId: 'WC251215000010',
        time: '08:30AM - 10:30AM',
        position: { lat: 49.2610, lng: -123.1110 },
        sequence: 1,
      },
      {
        id: '2002',
        category: 'premium',
        address: '88 Bayview Ave',
        orderId: 'WC251215000011',
        time: '11:00AM - 01:00PM',
        position: { lat: 49.2590, lng: -123.1090 },
        sequence: 2,
      },
    ],
  },
]

function buildMockBoardData(
  params: TasksMapBoardParams,
  people: TasksMapPersonInput[],
): TasksMapBoardData {
  const staffList = people.map((person) => ({
    id: Number(person.id),
    name: person.name,
    role: params.tab === 'technician' ? 'Technician' : 'Inspector',
    staff_no: `S${String(person.id).padStart(5, '0')}`,
    avatar: '',
    initials: person.initials ?? '',
    order_count: person.orderCount ?? person.orders.length,
    orders: person.orders.map((order) => ({
      index: order.sequence,
      id: Number(order.id) || order.id,
      order_no: order.orderId,
      order_id_text: order.orderId,
      address: order.address,
      full_address: order.address,
      time_start: order.time.split(' - ')[0] ?? '',
      time_end: order.time.split(' - ')[1] ?? '',
      time_text: order.time,
      latitude: order.position.lat,
      longitude: order.position.lng,
    })),
  }))

  const staffMarkers = people.map((person) => ({
    staff_id: Number(person.id),
    name: person.name,
    initials: person.initials ?? '',
    latitude: person.position.lat,
    longitude: person.position.lng,
  }))

  const orderMarkers = people.flatMap((person) =>
    person.orders.map((order) => ({
      staff_id: Number(person.id),
      index: order.sequence,
      order_id: order.orderId,
      latitude: order.position.lat,
      longitude: order.position.lng,
      address: order.address,
    })),
  )

  let driverRoutes: Array<Record<string, unknown>> = []
  if (params.selected_staff_id > 0) {
    const selected = people.find((person) => Number(person.id) === params.selected_staff_id)
    if (selected) {
      driverRoutes = selected.orders.map((order) => ({
        index: order.sequence,
        order_id: order.orderId,
        latitude: order.position.lat,
        longitude: order.position.lng,
      }))
    }
  }

  return {
    date: params.date,
    tab: params.tab,
    selected_staff_id: params.selected_staff_id,
    staff_list: staffList,
    map: {
      staff_markers: staffMarkers,
      order_markers: orderMarkers,
      driver_routes: driverRoutes,
    },
  }
}

/** 后端未部署时启用 mock */
export function shouldUseTasksMapMock(): boolean {
  return import.meta.env.VITE_TASKS_MAP_USE_MOCK === 'true'
}

/** 真实接口失败时（如 UTF-8 500）在开发环境回退 mock，不影响其他模块 */
export function shouldUseTasksMapFallbackMock(): boolean {
  if (import.meta.env.VITE_TASKS_MAP_FALLBACK_MOCK === 'false') return false
  if (import.meta.env.VITE_TASKS_MAP_FALLBACK_MOCK === 'true') return true
  return import.meta.env.DEV
}

export function getMockTasksMapMetaResponse(): MockApiResponse<TasksMapMetaData> {
  return {
    code: 200,
    msg: 'Tasks and Map metadata fetched successfully.',
    data: MOCK_TASKS_MAP_META,
  }
}

export function getMockTasksMapBoardResponse(
  params: TasksMapBoardParams,
): MockApiResponse<TasksMapBoardData> {
  const people = params.tab === 'technician' ? MOCK_TECHNICIANS : MOCK_INSPECTORS

  return {
    code: 200,
    msg: 'Tasks and map board fetched successfully.',
    data: buildMockBoardData(params, people),
  }
}
